import { useEffect, useMemo, useState, useCallback } from 'react';
import { useCatalog } from '../context/CatalogContext.jsx';
import { DEFAULT_CATALOG } from '../config/catalog';

const CUSTOM_FLAVOR_ID = 'custom';

const CUSTOM_FLAVOR = {
  id: CUSTOM_FLAVOR_ID,
  label: 'Como yo lo quiero',
  desc: 'Personalizá tu yogur',
  toppings: [],
  defaultAcidezIndex: 1,
  defaultTexturaIndex: 0,
  defaultGrasasIndex: 1,
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const clampIndex = (value, options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return 0;
  }
  if (value < 0) {
    return 0;
  }
  const maxIndex = options.length - 1;
  if (value > maxIndex) {
    return maxIndex;
  }
  return value;
};

const resolveMaxToppings = (catalogData) => {
  if (typeof catalogData?.limits?.maxToppings === 'number') {
    return catalogData.limits.maxToppings;
  }
  if (typeof DEFAULT_CATALOG?.limits?.maxToppings === 'number') {
    return DEFAULT_CATALOG.limits.maxToppings;
  }
  return 3;
};

const getAllowedToppingIds = (catalogData, sizeId) => {
  const sizes = ensureArray(catalogData.sizes).length
    ? catalogData.sizes
    : ensureArray(DEFAULT_CATALOG.sizes);
  const toppingsCatalog = ensureArray(catalogData.toppings).length
    ? catalogData.toppings
    : ensureArray(DEFAULT_CATALOG.toppings);

  const size = sizes.find((item) => item.id === sizeId);
  if (!size) {
    return toppingsCatalog.map((t) => t.id);
  }

  if (Array.isArray(size.allowedToppings)) {
    return size.allowedToppings;
  }

  return toppingsCatalog.map((t) => t.id);
};

const getFlavorsForSize = (catalogData, sizeId) => {
  const allowed = new Set(getAllowedToppingIds(catalogData, sizeId));
  const baseFlavors = ensureArray(catalogData.flavors).filter((flavor) =>
    ensureArray(flavor.toppings).every((id) => allowed.has(id))
  );

  return [...baseFlavors, CUSTOM_FLAVOR];
};

export const useYogurtBuilder = () => {
  const { catalog } = useCatalog();
  const data = catalog ?? DEFAULT_CATALOG;

  const sizes = useMemo(
    () => (ensureArray(data.sizes).length ? data.sizes : ensureArray(DEFAULT_CATALOG.sizes)),
    [data.sizes]
  );

  const toppingsCatalog = useMemo(
    () =>
      ensureArray(data.toppings).length ? data.toppings : ensureArray(DEFAULT_CATALOG.toppings),
    [data.toppings]
  );

  const acidezOptions = useMemo(
    () => (ensureArray(data.acidez).length ? data.acidez : ensureArray(DEFAULT_CATALOG.acidez)),
    [data.acidez]
  );

  const texturaOptions = useMemo(
    () => (ensureArray(data.textura).length ? data.textura : ensureArray(DEFAULT_CATALOG.textura)),
    [data.textura]
  );

  const grasasOptions = useMemo(
    () => (ensureArray(data.grasas).length ? data.grasas : ensureArray(DEFAULT_CATALOG.grasas)),
    [data.grasas]
  );

  const maxToppings = resolveMaxToppings(data);

  const defaultSize = sizes[0]?.id ?? '300';
  const defaultAllowedToppings = getAllowedToppingIds(data, defaultSize);
  const initialFlavors = getFlavorsForSize(data, defaultSize);
  const initialPresetFlavor = initialFlavors.find((flavor) => flavor.id !== CUSTOM_FLAVOR_ID);
  const initialFlavor = initialPresetFlavor ?? initialFlavors[0] ?? CUSTOM_FLAVOR;

  const initialAcidezIndex = clampIndex(
    initialFlavor?.defaultAcidezIndex ?? CUSTOM_FLAVOR.defaultAcidezIndex,
    acidezOptions
  );
  const initialTexturaIndex = clampIndex(
    initialFlavor?.defaultTexturaIndex ?? CUSTOM_FLAVOR.defaultTexturaIndex,
    texturaOptions
  );
  const initialGrasasIndex = clampIndex(
    initialFlavor?.defaultGrasasIndex ?? CUSTOM_FLAVOR.defaultGrasasIndex,
    grasasOptions
  );
  const initialToppings = ensureArray(initialFlavor?.toppings)
    .filter((id) => defaultAllowedToppings.includes(id))
    .slice(0, maxToppings);

  const [size, setSizeState] = useState(defaultSize);
  const [flavor, setFlavorState] = useState(initialFlavor?.id ?? CUSTOM_FLAVOR_ID);
  const [acidezIndex, setAcidezIndexState] = useState(initialAcidezIndex);
  const [texturaIndex, setTexturaIndexState] = useState(initialTexturaIndex);
  const [grasasIndex, setGrasasIndexState] = useState(initialGrasasIndex);
  const [toppings, setToppingsState] = useState(initialToppings);

  const allowedToppingIds = useMemo(
    () => getAllowedToppingIds(data, size),
    [data, size]
  );

  const availableToppings = useMemo(
    () => toppingsCatalog.filter((t) => allowedToppingIds.includes(t.id)),
    [toppingsCatalog, allowedToppingIds]
  );

  const availableFlavors = useMemo(
    () => getFlavorsForSize(data, size),
    [data, size]
  );

  const applyFlavorDefaults = useCallback(
    (selectedFlavor, targetSize) => {
      if (!selectedFlavor) {
        return;
      }
      const allowed = getAllowedToppingIds(data, targetSize);
      const defaults = ensureArray(selectedFlavor.toppings)
        .filter((id) => allowed.includes(id))
        .slice(0, maxToppings);
      const nextAcidez = clampIndex(
        selectedFlavor.defaultAcidezIndex ?? CUSTOM_FLAVOR.defaultAcidezIndex,
        acidezOptions
      );
      const nextTextura = clampIndex(
        selectedFlavor.defaultTexturaIndex ?? CUSTOM_FLAVOR.defaultTexturaIndex,
        texturaOptions
      );
      const nextGrasas = clampIndex(
        selectedFlavor.defaultGrasasIndex ?? CUSTOM_FLAVOR.defaultGrasasIndex,
        grasasOptions
      );

      setAcidezIndexState((prev) => (prev === nextAcidez ? prev : nextAcidez));
      setTexturaIndexState((prev) => (prev === nextTextura ? prev : nextTextura));
      setGrasasIndexState((prev) => (prev === nextGrasas ? prev : nextGrasas));
      setToppingsState((prev) => {
        const next = defaults;
        if (
          prev.length === next.length &&
          prev.every((value) => next.includes(value)) &&
          next.every((value) => prev.includes(value))
        ) {
          return prev;
        }
        return next;
      });
    },
    [data, maxToppings, acidezOptions, texturaOptions, grasasOptions]
  );

  useEffect(() => {
    setToppingsState((current) => {
      const filtered = current.filter((id) => allowedToppingIds.includes(id)).slice(0, maxToppings);
      if (
        filtered.length === current.length &&
        filtered.every((value, index) => value === current[index])
      ) {
        return current;
      }
      return filtered;
    });
  }, [allowedToppingIds, maxToppings]);

  useEffect(() => {
    if (flavor === CUSTOM_FLAVOR_ID) {
      return;
    }
    const selected = availableFlavors.find((item) => item.id === flavor);
    if (!selected || selected.id === CUSTOM_FLAVOR_ID) {
      const fallback = availableFlavors.find((item) => item.id !== CUSTOM_FLAVOR_ID);
      if (fallback) {
        setFlavorState(fallback.id);
        applyFlavorDefaults(fallback, size);
      } else {
        setFlavorState(CUSTOM_FLAVOR_ID);
      }
      return;
    }

    applyFlavorDefaults(selected, size);
  }, [flavor, availableFlavors, size, applyFlavorDefaults]);

  const handleSizeChange = (nextSize) => {
    setSizeState(nextSize);
  };

  const handleFlavorChange = (nextFlavorId) => {
    if (nextFlavorId === CUSTOM_FLAVOR_ID) {
      setFlavorState(CUSTOM_FLAVOR_ID);
      setToppingsState([]);
      return;
    }
    const selected = availableFlavors.find(
      (item) => item.id === nextFlavorId && item.id !== CUSTOM_FLAVOR_ID
    );
    if (!selected) {
      return;
    }
    setFlavorState(selected.id);
    applyFlavorDefaults(selected, size);
  };

  const convertToCustomIfNeeded = (isMatchingPreset) => {
    if (!isMatchingPreset && flavor !== CUSTOM_FLAVOR_ID) {
      setFlavorState(CUSTOM_FLAVOR_ID);
    }
  };

  const handleAcidezChange = (index) => {
    const clamped = clampIndex(index, acidezOptions);
    if (flavor !== CUSTOM_FLAVOR_ID) {
      const selected = availableFlavors.find((item) => item.id === flavor);
      const defaultIndex = selected?.defaultAcidezIndex ?? CUSTOM_FLAVOR.defaultAcidezIndex;
      convertToCustomIfNeeded(defaultIndex === clamped);
    }
    setAcidezIndexState(clamped);
  };

  const handleTexturaChange = (index) => {
    const clamped = clampIndex(index, texturaOptions);
    if (flavor !== CUSTOM_FLAVOR_ID) {
      const selected = availableFlavors.find((item) => item.id === flavor);
      const defaultIndex = selected?.defaultTexturaIndex ?? CUSTOM_FLAVOR.defaultTexturaIndex;
      convertToCustomIfNeeded(defaultIndex === clamped);
    }
    setTexturaIndexState(clamped);
  };

  const handleGrasasChange = (index) => {
    const clamped = clampIndex(index, grasasOptions);
    if (flavor !== CUSTOM_FLAVOR_ID) {
      const selected = availableFlavors.find((item) => item.id === flavor);
      const defaultIndex = selected?.defaultGrasasIndex ?? CUSTOM_FLAVOR.defaultGrasasIndex;
      convertToCustomIfNeeded(defaultIndex === clamped);
    }
    setGrasasIndexState(clamped);
  };

  const handleToggleTopping = (id) => {
    if (!allowedToppingIds.includes(id)) {
      return;
    }
    setToppingsState((current) => {
      let next;
      if (current.includes(id)) {
        next = current.filter((item) => item !== id);
      } else {
        if (current.length >= maxToppings) {
          return current;
        }
        next = [...current, id];
      }

      if (flavor !== CUSTOM_FLAVOR_ID) {
        const selected = availableFlavors.find((item) => item.id === flavor);
        const defaults = ensureArray(selected?.toppings)
          .filter((value) => allowedToppingIds.includes(value))
          .slice(0, maxToppings);
        const matchesDefaults =
          defaults.length === next.length &&
          defaults.every((value) => next.includes(value)) &&
          next.every((value) => defaults.includes(value));
        convertToCustomIfNeeded(matchesDefaults);
      }

      return next;
    });
  };

  const resetBuilder = () => {
    const resetSize = sizes[0]?.id ?? defaultSize;
    const flavorsForSize = getFlavorsForSize(data, resetSize);
    const preset = flavorsForSize.find((item) => item.id !== CUSTOM_FLAVOR_ID) ?? CUSTOM_FLAVOR;
    setSizeState(resetSize);
    setFlavorState(preset.id);
    applyFlavorDefaults(preset, resetSize);
  };

  const getCurrentProduct = () => ({
    size,
    flavor,
    toppings: [...toppings],
    acidezIndex,
    texturaIndex,
    grasasIndex,
    quantity: 1,
  });

  const isValid = () => availableFlavors.length > 0;

  return {
    size,
    flavor,
    customFlavorId: CUSTOM_FLAVOR_ID,
    toppings,
    acidezIndex,
    texturaIndex,
    grasasIndex,
    acidezOptions,
    texturaOptions,
    grasasOptions,
    MAX_TOPPINGS: maxToppings,
    availableFlavors,
    availableToppings,
    sizes,
    setSize: handleSizeChange,
    setFlavor: handleFlavorChange,
    setAcidezIndex: handleAcidezChange,
    setTexturaIndex: handleTexturaChange,
    setGrasasIndex: handleGrasasChange,
    toggleTopping: handleToggleTopping,
    resetBuilder,
    getCurrentProduct,
    isValid,
  };
};
